import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getTask, type TaskRecord } from '@/api/tasks';

/**
 * Phase 4 - Task 33 dev/test seam: Playwright specs (and Vitest specs that
 * mount React Query) need to swap a TaskRecord into the cache without going
 * through `/api/tasks/<id>` (the spec mocks the route, but `useTaskDetail`
 * caches the in-flight promise — flipping the cached value mid-test via the
 * queryClient is the cleanest way to simulate WS-pushed updates). This event
 * is dispatched by `window.__setTaskDetail(runId, task)` and listened for by
 * every active `useTaskDetail` hook below. The seam is gated on
 * `import.meta.env.MODE !== 'production'` so production bundles never expose
 * it (mirrors the Phase 3 `__pushChatStream` pattern in chatStream.store.ts).
 */
const TEST_SET_EVENT = 'wayne:test:setTaskDetail';

if (typeof window !== 'undefined' && import.meta.env.MODE !== 'production') {
  (window as unknown as {
    __setTaskDetail: (runId: string, task: unknown) => void;
  }).__setTaskDetail = (runId, task) => {
    const ev = new CustomEvent(TEST_SET_EVENT, { detail: { runId, task } });
    window.dispatchEvent(ev);
  };
}

export function useTaskDetail(runId: string | null) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (import.meta.env.MODE === 'production') return;
    if (!runId) return;
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<{ runId: string; task: unknown }>).detail;
      if (detail.runId === runId) {
        queryClient.setQueryData(['tasks', 'detail', runId], detail.task as TaskRecord);
      }
    };
    window.addEventListener(TEST_SET_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(TEST_SET_EVENT, handler as EventListener);
    };
  }, [queryClient, runId]);

  return useQuery<TaskRecord>({
    queryKey: ['tasks', 'detail', runId],
    queryFn: () => getTask(runId as string),
    enabled: Boolean(runId),
    staleTime: 2_000,
  });
}
